import vue, { onMounted, ref, Ref, watch } from 'vue';
import { alertController, loadingController } from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { Preferences } from '@capacitor/preferences';
import router from '@/router';

import { useSharedStore } from '@/stores/shared/sharedStore';
import { ValuesEnum } from '@/enums/values.enum';
import type { MinimumTopUpAmount } from '@/shared/interfaces/balance';
import type { TenantConfig } from '@/shared/interfaces/tenant';
import useSessionVariable from '@/shared/composables/useSessionVariable';
import useToast from '@/shared/composables/useToast';
import useUrlHelpers from '@/shared/composables/useUrlHelpers';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import { useI18n } from 'vue-i18n';

// Init
const { buildRedirectUrl } = useUrlHelpers();
const { guestTopUp, guestTopUpWalletPackage, topUp, topUpWalletPackage, selectedWallet } = useWallet();
const { presentToast } = useToast();
const { setSessionVariable } = useSessionVariable();
const { openURL, paymentMethodCountries } = useUtils();
const topUpType = ref('');

export type PreferredPaymentMode = 'payconiq' | 'click_to_pay' | null;

const buildPreferredPaymentPayload = (preferredPaymentMode: PreferredPaymentMode) => {
	return {
		payconiq: preferredPaymentMode === 'payconiq',
		click_to_pay: preferredPaymentMode === 'click_to_pay',
	};
};

const checkIsLatestAmountGreaterThanMinimumAmount = (latestAmount: string | number, minimumAmount: string | number): boolean => {
	try {
		const latestAmountValue = typeof latestAmount === 'string' ? parseInt(latestAmount, 10) : latestAmount;
		const latestMinimumAmountValue = typeof minimumAmount === 'string' ? parseInt(minimumAmount, 10) : minimumAmount;
		return latestAmountValue > latestMinimumAmountValue;
	} catch (e) {
		return true;
	}
};

const useTopUp = (tenantConfig: Ref<TenantConfig | undefined>, minimumTopUp: Ref<MinimumTopUpAmount | undefined>) => {
	// Init
	const enableTopUpSubmitButton = ref(false);
	const minimumAmountError = ref(false);
	const formValue = ref<string>('0.00');
	const selectedPackageId = ref(0);
	const paymentMethodCountry = ref();
	const paymentMethodCountryObject = ref();
	const defaultPaymentMethodCountry = ref();

	const sharedStore = useSharedStore();
	const { t } = useI18n();

	const availablePaymentCountries = paymentMethodCountries.filter((country) => {
		return tenantConfig.value!.payment_countries.includes(country.value);
	});

	watch(
		tenantConfig,
		(newValue) => {
			defaultPaymentMethodCountry.value =
				availablePaymentCountries.find((country) => {
					return country.value === newValue?.default_payment_country;
				}) ?? null;
		},
		{ immediate: true },
	);

	onMounted(() => {
		Preferences.get({ key: ValuesEnum.StoreSelectedPaymentCountry }).then((paymentCountry) => {
			// Overwrite only if value exists and is included on available payment countries from backend
			if (paymentCountry.value && availablePaymentCountries.find((country) => country.value === paymentCountry.value)) {
				// the payment country was already set, so let's update our internal state accordingly
				paymentMethodCountry.value = paymentCountry.value;
			} else {
				console.log('no payment country was found in storage, will just use default country.');
				countrySelectionValidated.value = false;
			}
		});
	});

	const persistDefaultCountryIfNoneWasSet = async () => {
		if (!paymentMethodCountry.value && !countrySelectionValidated.value) {
			console.log('setting default country', getApplicablePaymentCountry());
			await persistPaymentCountry(getApplicablePaymentCountry());
		}
	};

	const persistPaymentCountry = async (country: string) => {
		await Preferences.set({ key: ValuesEnum.StoreSelectedPaymentCountry, value: country });
	};

	const setCountryObject = (selectedCountry: string | null) => {
		paymentMethodCountryObject.value = availablePaymentCountries.find((country) => {
			return country.value === selectedCountry;
		});
	};

	watch(paymentMethodCountry, async (newValue, oldValue) => {
		if (oldValue !== newValue) {
			setCountryObject(newValue);
			countrySelectionValidated.value = true;
			await persistPaymentCountry(newValue);
		}
	});

	/**
	 * Fetches the appropriate payment country to use for the payment method once top up is initiated
	 */
	const getApplicablePaymentCountry = () => {
		console.log('getApplicablePaymentCountry', availablePaymentCountries, paymentMethodCountry.value);
		if (!availablePaymentCountries?.length) {
			return null;
		} else if (availablePaymentCountries.length === 1) {
			return availablePaymentCountries[0].value;
		} else {
			return paymentMethodCountry.value ?? defaultPaymentMethodCountry.value?.value ?? null;
		}
	};

	// we indicate the user has never really validated their country selection, so we know to prompt them if needed just to make sure
	const countrySelectionValidated = ref(true);

	const onAmountChange = (formValid: vue.ComputedRef<boolean>, fieldName: any, event: CustomEvent) => {
		onAmountInputChange(event.detail.value);
	};

	const onAmountInputChange = (rawAmount: string | number) => {
		const strAmount = String(rawAmount).replace(',', '.');

		const numericAmount = parseFloat(strAmount);

		if (isNaN(numericAmount)) {
			formValue.value = '0,00';
			enableTopUpSubmitButton.value = false;
			return;
		}

		formValue.value = numericAmount.toFixed(2).replace('.', ',');

		if (tenantConfig.value) {
			const minimumLocalAmount = minimumTopUp.value?.amount ?? 0;
			const topUpCost = Number(tenantConfig.value.top_up_cost) || 0;
			const totalAmount = Number((numericAmount + topUpCost).toFixed(2));
			if (minimumLocalAmount != 0) {
				const respectsMinimums = numericAmount >= minimumLocalAmount && totalAmount >= 1;

				enableTopUpSubmitButton.value = respectsMinimums;
				minimumAmountError.value = !respectsMinimums;
			} else {
				const respectsMinimums = totalAmount >= 1;

				enableTopUpSubmitButton.value = respectsMinimums;
				minimumAmountError.value = !respectsMinimums;
			}
		}
	};

	const selectTopUp = (walletPackageId: number, paymentMethodCountry: string, preferredPaymentMode: PreferredPaymentMode, encryptedRfidTag?: string, loginToken?: string) => {
		selectedPackageId.value = walletPackageId;
		startPackageTopUp(paymentMethodCountry, preferredPaymentMode, encryptedRfidTag, loginToken);
	};

	const executeTopUpRequest = async (amount: string, paymentMethodCountry: string | null, preferredPaymentMode: PreferredPaymentMode, encryptedRfidTag?: string, walletId?: string, currencyId?: number, loginToken?: string) => {
		const loading = await loadingController.create({});
		await loading.present();

		const redirectUrl = await buildRedirectUrl(preferredPaymentMode === 'payconiq');

		console.log('redirectUrl', redirectUrl);

		const data: any = {
			amount,
			redirectUrl,
			...buildPreferredPaymentPayload(preferredPaymentMode),
		};

		if (paymentMethodCountry) {
			data.country = paymentMethodCountry;
		}

		if (encryptedRfidTag) {
			data.encrypted_rfid_tag = encryptedRfidTag;
			data.currency_id = currencyId;
		}

		if (loginToken) {
			data.login_token = loginToken;
			data.currency_id = currencyId;
		}

		data.wallet_id = walletId;

		const badConnectionTimeout = setTimeout(async () => {
			// if we reach this, we don't have a response from the server yet
			await loading.dismiss();
			await (
				await alertController.create({
					header: t('checkout.errors.connection.header'),
					message: t('checkout.errors.connection.message'),
					buttons: [t('general.ok')],
				})
			).present();
		}, 15000);

		// Execute call
		try {
			const { redirectUrl } = encryptedRfidTag || loginToken ? await guestTopUp(data) : await topUp(data);

			clearTimeout(badConnectionTimeout);
			setSessionVariable(ValuesEnum.SessionTopUpSuccessData, JSON.stringify(data));

			// Set flag for Payconiq payment tracking
			if (preferredPaymentMode === 'payconiq') {
				setSessionVariable(ValuesEnum.SessionPayconiqPaymentInProgress, 'true');
			}

			await loading.dismiss();

			if (Capacitor.isNativePlatform()) {
				try {
					await AppLauncher.openUrl({ url: redirectUrl });
					return;
				} catch (e) {
					console.log(e);
				}
			}

			await openURL(redirectUrl);
		} catch (error: any) {
			await loading.dismiss();
			await presentToast('top', error, 5000, 'danger');
		}
	};

	const startTopUp = async (paymentMethodCountry: string, preferredPaymentMode: PreferredPaymentMode, encryptedRfidTag?: string, walletId?: string, currencyId?: number, loginToken?: string) => {
		const amount = String(formValue.value).replace(',', '.');
		await executeTopUpRequest(amount, paymentMethodCountry, preferredPaymentMode, encryptedRfidTag, walletId, currencyId, loginToken);
	};

	const startPackageTopUp = async (paymentMethodCountry: string, preferredPaymentMode: PreferredPaymentMode, encryptedRfidTag?: string, loginToken?: string) => {
		// Show loader
		const loading = await loadingController.create({});
		await loading.present();

		// Build redirect url
		const redirectUrl = await buildRedirectUrl(preferredPaymentMode === 'payconiq');

		// Create request body
		const data: any = {
			walletPackageId: selectedPackageId.value,
			redirectUrl,
			...buildPreferredPaymentPayload(preferredPaymentMode),
		};

		if (paymentMethodCountry) {
			data.country = paymentMethodCountry;
		}

		if (encryptedRfidTag) {
			data.encrypted_rfid_tag = encryptedRfidTag;
		}

		if (loginToken) {
			data.login_token = loginToken;
		}

		const badConnectionTimeout = setTimeout(async () => {
			// if we reach this, we don't have a response from the server yet
			await loading.dismiss();
			await (
				await alertController.create({
					header: t('checkout.errors.connection.header'),
					'message': t('checkout.errors.connection.message'),
					buttons: [t('general.ok')],
				})
			).present();
		}, 15000);

		// Execute call
		try {
			const response = encryptedRfidTag || loginToken ? await guestTopUpWalletPackage(data) : await topUpWalletPackage(data);

			clearTimeout(badConnectionTimeout);
			setSessionVariable(ValuesEnum.SessionTopUpSuccessData, JSON.stringify(data));

			// Set flag for Payconiq payment tracking
			if (preferredPaymentMode === 'payconiq') {
				setSessionVariable(ValuesEnum.SessionPayconiqPaymentInProgress, 'true');
			}

			await loading.dismiss();

			if (Capacitor.isNativePlatform()) {
				try {
					await AppLauncher.openUrl({ url: response.redirectUrl });
					return;
				} catch (e) {
					console.log(e);
				}
			}

			window.location.href = response.redirectUrl;
		} catch (error: any) {
			await loading.dismiss();
			await presentToast('top', error, 5000, 'danger');
		}
	};

	const switchPayconiqMode = () => {
		sharedStore.shouldUsePayconiq = !sharedStore.shouldUsePayconiq;
	};

	const switchPreferredPaymentMode = () => {
		sharedStore.shouldUsePreferredPaymentMode = !sharedStore.shouldUsePreferredPaymentMode;
		sharedStore.shouldUsePayconiq = sharedStore.shouldUsePreferredPaymentMode;
	};

	return {
		availablePaymentCountries,
		checkIsLatestAmountGreaterThanMinimumAmount,
		enableTopUpSubmitButton,
		formValue,
		minimumAmountError,
		onAmountChange,
		onAmountInputChange,
		paymentMethodCountry,
		paymentMethodCountryObject,
		selectTopUp,
		setFlag: setCountryObject,
		startTopUp,
		switchPreferredPaymentMode,
		switchPayconiqMode,
		topUpType,
		countrySelectionValidated,
		getApplicablePaymentCountry,
		defaultPaymentMethodCountry,
		persistDefaultCountryIfNoneWasSet,
	};
};

export default useTopUp;
